/**
 * FlowAIVoice — In-App Voice-Panel (#1385, Voice-Brain V1b).
 *
 * Sichtbarer Teil des kaskadierten Voice-Loops (V1a-Gateway /ws/voice, #1384):
 * Sprechen → STT → agent-loop (read-only) → TTS → Antwort-Audio.
 * Orb-Zustände idle/listening/thinking/speaking, Barge-in, Echo-Guard —
 * komplette Audio-/WS-Logik in useVoiceSession.
 *
 * #1387 (V1c-1): Action-Confirm-Anzeige — ein vorgeschlagener Telegram-Versand
 * (action.pending) wird als Karte mit Empfänger + Text gespiegelt; entschieden
 * wird AUSSCHLIESSLICH per gesprochenem Ja/Nein, die UI hat bewusst keine Buttons.
 *
 * #1391: Wake-Word/Standby (#1390) lebt NICHT mehr hier — der Dauer-Mikro-
 * Listener (useWakeWord) ist AUSSCHLIESSLICH an den Kiosk-Kontext /flow
 * gekoppelt (Datenschutz: permanentes Mikrofon nur am Kiosk-iPad). Dieses
 * Panel startet Sessions nur manuell, ohne Standby-Semantik.
 *
 * @page /flowai/voice
 * @since v2.641.0 (#1385)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Square, Send, Settings2, KeyRound } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useVoiceSession } from '../../hooks/useVoiceSession';
import FlowAvatarOrb from './FlowAvatarOrb';
import VoiceVisualPanel from './VoiceVisualPanel';

const API = import.meta.env.VITE_API_URL || '';

function fmtTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

// #1400 — plausible ElevenLabs voice_id (alphanumerisch, typ. 20 Zeichen)
const VOICE_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
const TTS_MODELS = ['eleven_turbo_v2_5', 'eleven_flash_v2_5', 'eleven_multilingual_v2'];

/**
 * #1400 — Admin-Card: ElevenLabs voice_id + TTS-Modell in der UI festlegen.
 * Speichert via bestehendem adminOnly PUT /api/einstellungen/:key (Cache wird
 * dort invalidiert); das Gateway liest beide Keys pro Session → kein Neustart.
 */
function VoiceSettingsCard() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiceId, setVoiceId] = useState('');
  const [savedVoiceId, setSavedVoiceId] = useState('');
  const [modelId, setModelId] = useState('eleven_turbo_v2_5');
  const [savedModelId, setSavedModelId] = useState('eleven_turbo_v2_5');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'error', text}

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setFeedback(null);
    Promise.all([
      fetch(`${API}/api/einstellungen/voice_tts_voice_id`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/einstellungen/voice_tts_model_id`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ]).then(([v, m]) => {
      const vid = typeof v?.value === 'string' ? v.value : '';
      const mid = typeof m?.value === 'string' && m.value ? m.value : 'eleven_turbo_v2_5';
      setVoiceId(vid); setSavedVoiceId(vid);
      setModelId(mid); setSavedModelId(mid);
    }).catch(() => setFeedback({ type: 'error', text: t('voice.settings.loadError') }))
      .finally(() => setLoading(false));
  }, [open, t]);

  const trimmed = voiceId.trim();
  const voiceIdValid = VOICE_ID_RE.test(trimmed);
  const dirty = trimmed !== savedVoiceId || modelId !== savedModelId;

  const save = async () => {
    if (!trimmed) { setFeedback({ type: 'error', text: t('voice.settings.emptyError') }); return; }
    if (!voiceIdValid) { setFeedback({ type: 'error', text: t('voice.settings.formatError') }); return; }
    setSaving(true);
    setFeedback(null);
    try {
      const calls = [];
      if (trimmed !== savedVoiceId) calls.push(['voice_tts_voice_id', trimmed]);
      if (modelId !== savedModelId) calls.push(['voice_tts_model_id', modelId]);
      for (const [key, value] of calls) {
        const r = await fetch(`${API}/api/einstellungen/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ value }),
        });
        if (!r.ok) throw new Error(`${key}: ${r.status}`);
      }
      setSavedVoiceId(trimmed);
      setSavedModelId(modelId);
      setFeedback({ type: 'ok', text: t('voice.settings.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('voice.settings.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        <Settings2 size={15} />
        {t('voice.settings.title')}
        <span className="ml-auto text-xs text-[var(--text-muted)]">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[var(--border-card)] pt-4">
          {loading ? (
            <div className="text-sm text-[var(--text-tertiary)]">{t('voice.settings.loading')}</div>
          ) : (
            <>
              <label className="block text-xs font-semibold uppercase tracking-[0.8px] text-[var(--text-secondary)]">
                {t('voice.settings.voiceId')}
              </label>
              <input
                type="text"
                value={voiceId}
                onChange={e => { setVoiceId(e.target.value); setFeedback(null); }}
                placeholder={t('voice.settings.voiceIdPlaceholder')}
                spellCheck={false}
                className="mt-1.5 w-full rounded-[8px] border border-[var(--border-card)] bg-[var(--bg-inset,#F8F9FC)] px-3 py-2 text-sm text-[var(--text-primary)] font-mono focus:outline-none focus:border-[#338edc]"
              />
              {trimmed && !voiceIdValid && (
                <div className="mt-1 text-xs" style={{ color: '#F59E0B' }}>{t('voice.settings.formatError')}</div>
              )}
              {!savedVoiceId && (
                <div className="mt-1 text-xs text-[var(--text-tertiary)]">{t('voice.settings.fallbackNote')}</div>
              )}

              <label className="block mt-4 text-xs font-semibold uppercase tracking-[0.8px] text-[var(--text-secondary)]">
                {t('voice.settings.model')}
              </label>
              <select
                value={modelId}
                onChange={e => { setModelId(e.target.value); setFeedback(null); }}
                className="mt-1.5 w-full rounded-[8px] border border-[var(--border-card)] bg-[var(--bg-inset,#F8F9FC)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              >
                {TTS_MODELS.map(m => (
                  <option key={m} value={m}>{m}{m === 'eleven_turbo_v2_5' ? ` (${t('voice.settings.default')})` : ''}</option>
                ))}
              </select>

              <div className="mt-3 text-xs text-[var(--text-tertiary)]">{t('voice.settings.hint')}</div>

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={save}
                  disabled={saving || !dirty || !trimmed || !voiceIdValid}
                  className="px-4 py-2 rounded-[10px] text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: 'var(--primary, #338edc)' }}
                >
                  {saving ? t('voice.settings.saving') : t('voice.settings.save')}
                </button>
                {feedback && (
                  <span className="text-sm" style={{ color: feedback.type === 'ok' ? '#10B981' : '#EF4444' }}>
                    {feedback.text}
                  </span>
                )}
              </div>
              <div className="mt-2 text-xs text-[var(--text-muted)]">{t('voice.settings.appliesNote')}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * #1391 — Admin-Card: Flow-Kiosk-PIN (/flow) setzen/rotieren.
 * PUT /api/flow/pin ist adminOnly; gespeichert wird NUR der bcrypt-Hash.
 */
function FlowKioskPinCard() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [pinSet, setPinSet] = useState(null);
  const [pin, setPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'error', text}

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/flow/pin`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setPinSet(!!d?.pin_set))
      .catch(() => {});
  }, [open]);

  const valid = /^\d{8}$/.test(pin);

  const save = async () => {
    if (!valid) { setFeedback({ type: 'error', text: t('flow.admin.formatError') }); return; }
    setSaving(true);
    setFeedback(null);
    try {
      const r = await fetch(`${API}/api/flow/pin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setPin('');
      setPinSet(true);
      setFeedback({ type: 'ok', text: t('flow.admin.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('flow.admin.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        <KeyRound size={15} />
        {t('flow.admin.title')}
        <span className="ml-auto text-xs text-[var(--text-muted)]">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[var(--border-card)] pt-4">
          <div className="text-xs text-[var(--text-tertiary)]">
            {pinSet === null ? '…' : pinSet ? t('flow.admin.pinSet') : t('flow.admin.pinUnset')}
          </div>
          <label className="block mt-3 text-xs font-semibold uppercase tracking-[0.8px] text-[var(--text-secondary)]">
            {t('flow.admin.pinLabel')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={pin}
            maxLength={8}
            onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 8)); setFeedback(null); }}
            placeholder="••••••••"
            spellCheck={false}
            autoComplete="off"
            className="mt-1.5 w-full rounded-[8px] border border-[var(--border-card)] bg-[var(--bg-inset,#F8F9FC)] px-3 py-2 text-sm text-[var(--text-primary)] font-mono tracking-[4px] focus:outline-none focus:border-[#338edc]"
          />
          {pin && !valid && (
            <div className="mt-1 text-xs" style={{ color: '#F59E0B' }}>{t('flow.admin.formatError')}</div>
          )}
          <div className="mt-3 text-xs text-[var(--text-tertiary)]">{t('flow.admin.hint')}</div>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving || !valid}
              className="px-4 py-2 rounded-[10px] text-sm font-medium text-white disabled:opacity-50"
              style={{ background: 'var(--primary, #338edc)' }}
            >
              {saving ? t('flow.admin.saving') : t('flow.admin.save')}
            </button>
            {feedback && (
              <span className="text-sm" style={{ color: feedback.type === 'ok' ? '#10B981' : '#EF4444' }}>
                {feedback.text}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FlowAIVoice() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { state, amplitude, elapsed, maxSeconds, turns, error, endReason, pendingAction, actionResult, visual, start, stop } = useVoiceSession();
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [turns]);

  const active = state !== 'idle';
  const statusTexts = useMemo(() => ({
    idle: t('voice.status.idle'),
    connecting: t('voice.status.connecting'),
    listening: t('voice.status.listening'),
    thinking: t('voice.status.thinking'),
    speaking: t('voice.status.speaking'),
  }), [t]);

  // #1399 has-overlay-Muster: Visual eingeblendet → Avatar rückt zur Seite.
  const hasOverlay = !!visual;

  return (
    <div className={`p-6 mx-auto ${hasOverlay ? 'max-w-6xl' : 'max-w-3xl'}`}>
      <style>{`.flow-avatar{min-height:${hasOverlay ? 180 : 300}px;width:100%}`}</style>

      <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('voice.title')}</h1>
      <p className="text-sm text-[var(--text-tertiary)] mt-1">{t('voice.subtitle')}</p>

      <div className={`mt-6 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl p-8 flex flex-col ${hasOverlay ? 'md:flex-row md:items-start gap-8' : 'items-center'}`}>
        <div className={`flex flex-col items-center ${hasOverlay ? 'md:w-[260px] shrink-0' : 'w-full'}`}>
        {/* FlowAvatar (#1393) — Status-Zeile rendert die Komponente selbst */}
        <FlowAvatarOrb state={state} amplitude={amplitude} size={hasOverlay ? 110 : 140} statusTexts={statusTexts} />

        {/* Timer */}
        <div className="mt-3 text-xs text-[var(--text-muted)]">
          {active
            ? `${fmtTime(elapsed)}${maxSeconds ? ` / ${fmtTime(maxSeconds)}` : ''}`
            : endReason ? t('voice.sessionEnded') : '\u00A0'}
        </div>

        {/* Start/Stop — Standby/Wake-Word gibt es hier seit #1391 nicht mehr (nur /flow) */}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={active ? stop : () => start()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-sm font-medium text-white"
            style={{ background: active ? '#EF4444' : 'var(--primary, #338edc)' }}
          >
            {active ? <Square size={16} /> : <Mic size={16} />}
            {active ? t('voice.stop') : t('voice.start')}
          </button>
        </div>

        {/* #1387 — Action-Confirm: Vorschlag spiegeln, Entscheidung NUR per Sprache */}
        {pendingAction && (
          <div className="mt-4 w-full text-sm rounded-[10px] border px-4 py-3"
            style={{ borderColor: '#338edc', background: 'rgba(51,142,220,0.06)' }}>
            <div className="flex items-center gap-2 font-medium text-[var(--text-primary)]">
              <Send size={14} />
              {t('voice.action.pendingTitle')}
            </div>
            <div className="mt-2 text-[var(--text-secondary)]">
              <span className="font-medium">{t('voice.action.recipient')}:</span> {pendingAction.recipient}
            </div>
            <div className="mt-1 text-[var(--text-secondary)] whitespace-pre-wrap">
              <span className="font-medium">{t('voice.action.message')}:</span> {pendingAction.message}
            </div>
            <div className="mt-2 text-xs text-[var(--text-tertiary)]">{t('voice.action.confirmHint')}</div>
          </div>
        )}
        {!pendingAction && actionResult && (
          <div className="mt-4 w-full text-sm rounded-[10px] border px-4 py-3"
            style={{
              borderColor: actionResult.status === 'sent' ? '#10B981' : '#E5E7EB',
              background: actionResult.status === 'sent' ? 'rgba(16,185,129,0.06)' : 'var(--bg-inset, #F8F9FC)',
              color: 'var(--text-secondary)',
            }}>
            {t(`voice.action.result.${actionResult.status}`) !== `voice.action.result.${actionResult.status}`
              ? t(`voice.action.result.${actionResult.status}`)
              : t('voice.action.result.failed')}
          </div>
        )}

        {/* Fehler (lokalisiert über Code — keine Provider-Details, AK6) */}
        {error && (
          <div className="mt-4 w-full text-sm rounded-[10px] border px-4 py-3"
            style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)', color: 'var(--text-secondary)' }}>
            {t(`voice.error.${error.code}`) !== `voice.error.${error.code}`
              ? t(`voice.error.${error.code}`)
              : t('voice.error.unknown')}
          </div>
        )}
        </div>

        {/* #1399 Voice-Visual — Overlay-Panel neben (md+) bzw. unter dem Avatar */}
        {hasOverlay && (
          <div className="flex-1 min-w-0 mt-6 md:mt-0">
            <VoiceVisualPanel visual={visual} />
          </div>
        )}
      </div>

      {/* Mitschrift (dezent, intern hilfreich) */}
      {turns.length > 0 && (
        <div className="mt-4 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-secondary)] mb-3">
            {t('voice.transcript')}
          </div>
          <div ref={logRef} className="max-h-64 overflow-y-auto space-y-2 text-sm">
            {turns.map((turn, i) => (
              <div key={`${i}-${turn.role}`} className="flex gap-2">
                <span className="shrink-0 font-medium text-[var(--text-tertiary)]">
                  {turn.role === 'user' ? t('voice.you') : t('voice.assistant')}:
                </span>
                <span className="text-[var(--text-primary)] whitespace-pre-wrap">{turn.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* #1400 — Voice-Einstellungen (nur Admin; Endpoint ist zusätzlich adminOnly) */}
      {user?.rolle === 'administrator' && <VoiceSettingsCard />}
      {/* #1391 — Flow-Kiosk-PIN-Rotation (nur Admin; Endpoint ist zusätzlich adminOnly) */}
      {user?.rolle === 'administrator' && <FlowKioskPinCard />}
    </div>
  );
}
